import React, { forwardRef, useEffect, useState, useRef } from 'react'
import { CalendarIcon } from '@heroicons/react/outline'
import Datepicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import PropTypes from 'prop-types'
import clsx from 'clsx'

const InputDate = ({
    onChange,
    label,
    isRequired,
    className,
    error,
    initialValue,
}) => {
    const [startDate, setStartDate] = useState(
        initialValue ? new Date(initialValue) : new Date(),
    )
    const isUpdatingFromProps = useRef(false)
    const isFirstRender = useRef(true)

    // Update internal state when initialValue changes (e.g., form reset)
    useEffect(() => {
        if (initialValue !== undefined && initialValue !== '') {
            const newDate = new Date(initialValue)
            if (newDate.getTime() !== startDate.getTime()) {
                isUpdatingFromProps.current = true
                setStartDate(newDate)
            }
        } else if (initialValue === '') {
            // Reset to current date when form is cleared
            isUpdatingFromProps.current = true
            setStartDate(new Date())
        }
    }, [initialValue])

    useEffect(() => {
        // Skip onChange on first render
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        if (isUpdatingFromProps.current) {
            isUpdatingFromProps.current = false
            return
        }
        if (onChange) {
            onChange(startDate)
        }
    }, [startDate])

    // eslint-disable-next-line react/display-name, react/prop-types
    const CustomInput = forwardRef(({ value, onClick }, ref) => (
        <div
            onClick={onClick}
            ref={ref}
            className="relative flex-grow w-full h-12 flex flex-row items-center justify-start border border-grayLines rounded-xl overflow-hidden"
        >
            <div className="h-full w-14 bg-pageBackground flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-textDarkGray" />
            </div>
            <label
                role="button"
                className="flex-grow px-2 h-full font-medium text-sm text-textDarkerGray flex items-center"
            >
                {value}
            </label>
        </div>
    ))

    return (
        <div
            className={clsx(
                'w-full flex-grow flex items-start justify-center flex-col',
                className,
            )}
        >
            {label && (
                <label
                    htmlFor={name}
                    className={clsx(
                        'w-auto h-auto text-sm font-semibold text-left mb-2 text-textDarkerGray',
                    )}
                >
                    {label}
                    {isRequired && <span className="text-error ml-1">*</span>}
                </label>
            )}
            <Datepicker
                portalId="root-portal"
                minDate={new Date()}
                className="w-full h-12"
                selected={startDate}
                onChange={date => setStartDate(date)}
                customInput={<CustomInput />}
                popperPlacement="bottom-start"
                popperClassName="z-150"
            />
            <p
                className={clsx(
                    error ? 'visible' : 'invisible',
                    'w-full h-4 text-right text-error font-medium text-xs mt-1',
                )}
            >
                {error}
            </p>
        </div>
    )
}

InputDate.propTypes = {
    onChange: PropTypes.func,
    label: PropTypes.string,
    isRequired: PropTypes.bool,
    className: PropTypes.oneOfType([PropTypes.any, PropTypes.string]),
    error: PropTypes.string,
    initialValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
    ]),
}

export default InputDate
